import { LosseLink, useLoaderData, useLocation } from '@ubo/losse-sjedel'
import type { HeaderLoaderData } from './Header'
import type { Component_Header_Menu } from '~/graphql/types'
import clsx from 'clsx'
import { useEffect, useState } from 'react'
import { AnimatePresence, motion } from 'framer-motion'

function hasSubmenuItems(menu: Component_Header_Menu | null | undefined) {
  return Boolean(menu?.submenu?.some((item) => item?.link))
}

function ChevronIcon({ open }: { open: boolean }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      className={clsx(open ? 'rotate-0' : '-rotate-90', 'transition-transform duration-200')}
      aria-hidden
    >
      <path
        d="M18.7098 8.2101C18.6169 8.11638 18.5063 8.04198 18.3844 7.99121C18.2625 7.94044 18.1318 7.91431 17.9998 7.91431C17.8678 7.91431 17.7371 7.94044 17.6152 7.99121C17.4934 8.04198 17.3828 8.11638 17.2898 8.2101L12.7098 12.7901C12.6169 12.8838 12.5063 12.9582 12.3844 13.009C12.2625 13.0598 12.1318 13.0859 11.9998 13.0859C11.8678 13.0859 11.7371 13.0598 11.6152 13.009C11.4934 12.9582 11.3828 12.8838 11.2898 12.7901L6.70982 8.2101C6.61685 8.11638 6.50625 8.04198 6.38439 7.99121C6.26253 7.94044 6.13183 7.91431 5.99982 7.91431C5.8678 7.91431 5.7371 7.94044 5.61524 7.99121C5.49338 8.04198 5.38278 8.11638 5.28982 8.2101C5.10356 8.39747 4.99902 8.65092 4.99902 8.9151C4.99902 9.17929 5.10356 9.43274 5.28982 9.6201L9.87982 14.2101C10.4423 14.7719 11.2048 15.0875 11.9998 15.0875C12.7948 15.0875 13.5573 14.7719 14.1198 14.2101L18.7098 9.6201C18.8961 9.43274 19.0006 9.17929 19.0006 8.9151C19.0006 8.65092 18.8961 8.39747 18.7098 8.2101Z"
        fill="currentColor"
      />
    </svg>
  )
}

export default function Menu() {
  const { header } = useLoaderData<HeaderLoaderData>()
  const location = useLocation()
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null)
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null)

  useEffect(() => {
    setExpandedIndex(null)
    setHoveredIndex(null)
  }, [location.pathname, location.hash, location.search])

  return (
    <>
      {header?.menu?.map((edge, index) => {
        const isLast = index === (header?.menu?.length ?? 0) - 1
        const hasSubmenu = !isLast && hasSubmenuItems(edge)
        const isExpanded = expandedIndex === index

        const linkClassName = clsx(
          'max-lg:animate-fade-left',
          isLast
            ? 'btn xl:ml-4 max-lg:mt-6'
            : 'uppercase hover:underline max-lg:font-bold text-3xl lg:text-sm xl:text-base font-blitz-futura font-normal'
        )

        const animationStyle = {
          animationDelay: `${index * 150}ms`
        }

        if (!hasSubmenu) {
          return (
            <li key={index} style={animationStyle}>
              <LosseLink className={linkClassName} src={edge?.link} />
            </li>
          )
        }

        const submenuItems = edge?.submenu?.filter((item) => item?.link) ?? []

        const isHovered = hoveredIndex === index

        return (
          <li
            key={index}
            className="relative max-lg:w-full max-lg:animate-fade-left"
            style={animationStyle}
            onMouseEnter={() => setHoveredIndex(index)}
            onMouseLeave={() => setHoveredIndex(null)}
          >
            <div className="flex items-center justify-between gap-4 lg:block">
              <LosseLink className={clsx(linkClassName, 'max-lg:flex-1')} src={edge?.link} />
              <button
                type="button"
                onClick={() => setExpandedIndex(isExpanded ? null : index)}
                aria-expanded={isExpanded}
                aria-label="Submenu openen"
                className="flex-shrink-0 p-2 -mr-2 text-blitz-shark lg:hidden"
              >
                <ChevronIcon open={isExpanded} />
              </button>
            </div>

            <AnimatePresence initial={false}>
              {isExpanded && (
                <motion.ul
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.2 }}
                  className="overflow-hidden lg:hidden mt-3 ml-1 flex flex-col gap-1 border-l-2 border-blitz-nougat pl-4"
                >
                  {submenuItems.map((subItem, subIndex) => (
                    <li key={subIndex}>
                      <LosseLink
                        className="block py-2 text-xl font-normal text-blitz-shark/75 hover:text-blitz-shark hover:underline font-blitz-futura"
                        src={subItem?.link}
                      />
                    </li>
                  ))}
                </motion.ul>
              )}
            </AnimatePresence>

            <div
              className={clsx(
                'absolute left-0 top-full z-[60] hidden pt-3 transition-all duration-200 lg:block',
                isHovered ? 'pointer-events-auto translate-y-0 opacity-100' : 'pointer-events-none translate-y-1 opacity-0'
              )}
            >
              <ul className="min-w-[200px] overflow-hidden rounded-sm border border-blitz-spring-wood bg-white py-1 shadow-lg">
                {submenuItems.map((subItem, subIndex) => (
                  <li key={subIndex}>
                    <LosseLink
                      className="block whitespace-nowrap px-5 py-2.5 text-sm uppercase font-blitz-futura font-normal text-blitz-shark hover:bg-blitz-spring-wood hover:underline"
                      src={subItem?.link}
                    />
                  </li>
                ))}
              </ul>
            </div>
          </li>
        )
      })}
    </>
  )
}
